<script>
import moment from 'moment';
import { get, find, mapValues, each, first } from 'lodash';

import { SUBSCRIPTION_PLAN_TYPE } from 'constants/values';

import NuttremaLayoutThreeVertical from 'components/ui/layout/nuttrema-layout-three-vertical';

export default {
  name: 'cmpt-subscription-east-summary',
  // mixins: [SubscriptionSummary],
  props: {
    summary: {
      required: true,
      default: null,
    },
    preconditions: {
      required: false,
      default: null,
    },
    autoRenewal: {
      required: false,
      default: false,
    },
    currencyID: {
      required: false,
      default: false,
    },
    paymentPlanID: {
      required: false,
      default: false,
    },
    memberOfEU: {
      required: false,
      default: true,
    },
    taxRate: {
      required: false,
      default: 0,
    },
    processing: {
      required: false,
      default: false,
    },
    theme: {
      required: false,
      default: null,
    },
  },
  components: {
    NuttremaLayoutThreeVertical,
  },
  data() {
    return {
      show: {
        planDescription: false,
      },
    };
  },
  methods: {
    previousStep() {
      this.$emit('previousStep');
    },
    cancel() {
      this.$emit('cancel');
    },
    nextStep() {
      this.$emit('nextStep');
    },
    proceedWithPayment() {
      this.$emit('proceedWithPayment');
    },
    calculateExtraFeaturesPrice() {
      const subscribedFeatures = get(this.summary, 'SubscribedFeatures', []);

      const prices = mapValues(subscribedFeatures, (item) => {
        const subscribedFeatureOptions = get(item, 'SubscribedFeatureOptions', []);

        const priceOption = find(subscribedFeatureOptions, (paymentPlan) => {
          const priceOptions = get(paymentPlan, 'PriceOptions', []);
          return find(priceOptions, priceOption => get(priceOption, 'PaymentPlan.id') === this.paymentPlanID);
        });

        return first(get(priceOption, 'PriceOptions'));
      });

      let sum = 0;

      each(prices, (item) => {
        const price = get(item, 'price', 0);
        sum += Number(price);
      });

      return this.calculateInvoiceItemAmounts(sum, this.taxRate);
    },
    calculateInvoiceItemAmounts(price, taxRate) {
      let invoiceItemAmounts = {
        preTaxAmount: null,
        taxRate: null,
        tax: null,
        price: null,
      };

      try {
        price = parseFloat(price);
        taxRate = parseFloat(taxRate);

        if (isNaN(price)) throw new Error('Invalid input data for calculation.');

        const preTaxAmount = price / (1 + (taxRate / 100));
        const tax = price - preTaxAmount;

        invoiceItemAmounts = {
          preTaxAmount,
          taxRate,
          tax,
          price,
        };
      } catch (error) {
        console.error(error);
      }

      return invoiceItemAmounts;
    },
  },
  computed: {
    priceOption() {
      const priceOptions = get(this.summary, 'PriceOptions', []);
      return find(priceOptions, item => get(item, 'PaymentPlan.id') === this.paymentPlanID);
    },
    duration() {
      const startOn = get(this.preconditions, 'start_on', new Date());
      const duration = get(this.priceOption, 'PaymentPlan.months', 0);
      const endOn = moment(startOn).add(duration, 'months');
      return {
        start_on: moment(startOn).format('YYYY-MM-DD'),
        end_on: moment(endOn).format('YYYY-MM-DD'),
      };
    },
    invoiceItemAmounts() {
      const priceOption = get(this.priceOption, 'price', 0);
      const price = this.calculateInvoiceItemAmounts(priceOption, this.taxRate);
      const extraOptions = this.calculateExtraFeaturesPrice();

      const subTotal = get(price, 'preTaxAmount', 0) + get(extraOptions, 'preTaxAmount', 0);
      const total = get(price, 'price', 0) + get(extraOptions, 'price', 0);
      const vat = total - subTotal;

      return {
        price,
        extraOptions,
        subTotal,
        vat,
        total,
      };
    },
    isTrial() {
      return get(this.summary, 'SubscriptionPlanType.code') === SUBSCRIPTION_PLAN_TYPE.TRIAL;
    },
  },
};
</script>

<style lang="scss" scoped>
  .subscription-east-summary {
    display: flex;
    flex: 1 0 auto;
    padding: 0 0.75em;

    .subscription-east-summary__content {
      width: 100%;
      height: 100%;
      display: flex;
      flex: 1 0 100%;
      flex-direction: column;
      padding-bottom: 1em;

      .sticky & {
        z-index: 1001;
        position: relative;
      }

      .subscription-east-summary__title {
        color: #465568;
        text-align: left;
        font-weight: 500;
        font-size: 1.05em;
        margin: 0.35em 0;
      }

      .subscription-east-summary__item {
        display: flex;
        flex: 0 0 auto;
        flex-direction: column;

        font-size: 0.9em;
        border-bottom: 1px solid #909aa5;

        &:last-child {
          border-bottom: none;
        }

        .item__label {
          display: flex;
          flex: 1 0 auto;
          justify-content: space-between;
          padding: 0.25em 0;
        }

        .item__title {
          position: relative;
        }

        .item__value {
          a {
            color: #607d8b;
            margin-right: 0.25em;
          }
        }

        .item__description {
          display: flex;
          flex: 1 1 100%;
          flex-direction: column;

          padding: 0 0 0.25em 0;
          border-top: 1px solid #e7e7e7;

          p {
            font-weight: 600;
            margin: 0.25em 0;
          }

          ul > li {
            margin-bottom: 0.25em;
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
        // end ~ item__description

        .item__actions {
          display: flex;
          flex: 1 1 100%;
          flex-direction: column;
          margin-top: 2em;
          > * {
            position: relative;
            margin-bottom: 0.5em;
            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        .el-button--text {
          color: #607d8b;
        }

        .item__agreement {
          /deep/ p {
            color: #465568;
            text-align: center;
          }
          /deep/ p a {
            color: #465568;
            text-decoration: underline;
            font-weight: 500;
          }
        }


        &.is-sub-total {
          .item__label {
            font-weight: 600;
            text-transform: uppercase;
          }
        }
        &.is-total {
          .item__label {
            font-weight: 600;
            font-size: 1.25em;
            text-transform: uppercase;
          }
        }
      }
      // end ~ subscription-east-summary__item

    } // end ~ subscription-east-summary__content

    .subscription-east-summary__footer {
      display: flex;
      flex: 1 0 100%;
      align-items: center;
      flex-direction: column;
      justify-content: center;
      p {
        color: #607d8b;
        font-size: 0.8em;
        margin-top: 0.5em;
        text-align: center;
      }
    }

    &.theme-global {
      .nuttrema-theme-professional-turquoise & {
        .subscription-east-summary__title {
          color: #fff;
        }

        .item__agreement {
          /deep/ p, /deep/ p a {
            color: #fff;
          }
        }

        .subscription-east-summary__item {
          color: #fff;
          border-bottom: 1px solid #fff;

          &:last-child {
            border-bottom: none;
          }

          .item__value, .item__value a, .el-button--text {
            color: #fff;
          }
        }
      }
      // end ~ .nuttrema-theme-professional-turquoise

      .nuttrema-theme-professional-topography & {

      }
    }

    &.is-in-new-purchase {
      .subscription-east-summary__content {
        margin-top: 1em;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/ui/subscription/abstract-subscription-east-summary.vue